import { createAsyncThunk } from "@reduxjs/toolkit"

import {
    IOneVerifyUpdateRoleDataUnmapped,
    IOneVerifyUserInviteDataUnmapped,
} from "@common-shared/types/requestData/oneVerifyRequestData.types.ts"
import {
    IOneVerifyOrganizationUser,
    IOneVerifyOrganizationUserInvite,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"

import { OrganizationUsersService } from "../../services/organizationUsers.service"
import type {
    IThunkActionWithPracticeData,
    IThunkActionWithPracticeOrIdData,
} from "../../types/general.types.ts"
import {
    organizationInvitesCreateDone,
    organizationInvitesDeleteDone,
    organizationInvitesSetList,
    organizationInvitesUpdateDone,
    organizationUsersDeleteDone,
    organizationUsersSetList,
    organizationUsersUpdateDone,
} from "../slices/organization.slice"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"

/**
 * Get list of organization users
 */
export const organizationUsersGetList = createAsyncThunk<
    IOneVerifyOrganizationUser[],
    IThunkActionWithPracticeOrIdData<undefined>
>("organizationUsersGetListPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("organizationUsersGetListLoading"))

    try {
        const result = await OrganizationUsersService.getUsersList(
            action.practice,
        )

        if (result) {
            dispatch(organizationUsersSetList(result))

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || []
    } catch (e) {
        return []
    } finally {
        dispatch(runtimeStopLoading("organizationUsersGetListLoading"))

        action.onFinally?.()
    }
})

/**
 * Delete organization user
 */
export const organizationUsersDelete = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOneVerifyOrganizationUser>
>("organizationUsersDeletePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("organizationUsersDeleteLoading"))

    try {
        const result = await OrganizationUsersService.deleteUser(
            action.practice,
            action.payload,
        )

        if (result) {
            dispatch(organizationUsersDeleteDone(action.payload))

            ToastrHelper.success(
                "User was successfully removed from organization",
            )

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("organizationUsersDeleteLoading"))

        action.onFinally?.()
    }
})

/**
 * Reset user's MFA setup
 */
export const organizationUsersResetMFA = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOneVerifyOrganizationUser>
>("organizationUsersResetMFAPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("organizationUsersResetMFALoading"))

    try {
        const result = await OrganizationUsersService.resetUserMFASetup(
            action.practice,
            action.payload,
        )

        if (result) {
            ToastrHelper.success("User's MFA setup was successfully reset")

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("organizationUsersResetMFALoading"))

        action.onFinally?.()
    }
})

/**
 * Change role of organization user
 */
export const organizationUsersChangeRole = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<
        IOneVerifyUpdateRoleDataUnmapped & { user: IOneVerifyOrganizationUser }
    >
>("organizationUsersChangeRolePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("organizationUsersChangeRoleLoading"))

    try {
        const { user, ...roleData } = action.payload

        const result = await OrganizationUsersService.changeUserRole(
            action.practice,
            user,
            roleData,
        )

        if (result) {
            dispatch(organizationUsersUpdateDone(result))

            ToastrHelper.success("User's role was successfully changed")

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("organizationUsersChangeRoleLoading"))

        action.onFinally?.()
    }
})

/**
 * Get list of user invites
 */
export const organizationInvitesGetList = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<undefined>
>("organizationInvitesGetListPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("organizationInvitesGetListLoading"))

    try {
        const result = await OrganizationUsersService.getInvitesList(
            action.practice,
        )

        if (result) {
            dispatch(organizationInvitesSetList(result))

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("organizationInvitesGetListLoading"))

        action.onFinally?.()
    }
})

/**
 * Invite user to organization
 */
export const organizationInvitesCreate = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOneVerifyUserInviteDataUnmapped>
>("organizationInvitesCreatePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("organizationInvitesCreateLoading"))

    try {
        const result = await OrganizationUsersService.inviteUser(
            action.practice,
            action.payload,
        )

        if (result) {
            dispatch(
                result.isNew
                    ? organizationInvitesCreateDone(result.invite)
                    : organizationInvitesUpdateDone(result.invite),
            )

            ToastrHelper.success(
                "User was successfully invited to join your organization",
            )

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("organizationInvitesCreateLoading"))

        action.onFinally?.()
    }
})

/**
 * Delete user invite
 */
export const organizationInvitesDelete = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<IOneVerifyOrganizationUserInvite>
>("organizationInvitesDeletePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("organizationInvitesDeleteLoading"))

    try {
        const result = await OrganizationUsersService.deleteUserInvite(
            action.practice,
            action.payload,
        )

        if (result) {
            dispatch(organizationInvitesDeleteDone(action.payload))

            ToastrHelper.success("User invitation was successfully removed")

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("organizationInvitesDeleteLoading"))

        action.onFinally?.()
    }
})

/**
 * Change role of user invite
 */
export const organizationInvitesChangeRole = createAsyncThunk<
    void,
    IThunkActionWithPracticeData<
        IOneVerifyUpdateRoleDataUnmapped & {
            invite: IOneVerifyOrganizationUserInvite
        }
    >
>("organizationInvitesChangeRolePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("organizationInvitesChangeRoleLoading"))

    try {
        const { invite, ...roleData } = action.payload

        const result = await OrganizationUsersService.changeUserInviteRole(
            action.practice,
            invite,
            roleData,
        )

        if (result) {
            dispatch(organizationInvitesUpdateDone(result))

            ToastrHelper.success(
                "User invitation's role was successfully changed",
            )

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("organizationInvitesChangeRoleLoading"))

        action.onFinally?.()
    }
})
